/**
 * Footer newsletter sign-up, submitted in place.
 *
 * Shopify's `{% form 'customer' %}` posts to /contact and answers with a whole
 * new document, so subscribing threw the reader back to the top of a reloaded
 * page — from the footer, which is the one place on the site where that is
 * most obviously wrong.
 *
 * The form, its fields and its markup are untouched: this intercepts the
 * submit, posts the very same FormData to the very same action, and lifts
 * Shopify's own rendered message out of the response. So the wording still
 * comes from the section's settings and from Shopify's validation — nothing is
 * re-implemented here, and the error text a shopper sees is the same one the
 * full page load would have shown.
 *
 * Two things can still force a full navigation, and both are Shopify's:
 *
 *   - With spam protection on, Shopify injects a "form-persister" that binds
 *     its own submit listener to every customer form and calls a native
 *     submit whatever anyone else did with the event. This listener is
 *     registered for the capture phase so it runs first, and it stops the
 *     event there so the persister never gets its turn.
 *   - The server may answer the post with its bot challenge, or refuse a
 *     background post outright (it does, while spam protection is on).
 *     Neither can be shown in place, so the form is then handed back to the
 *     browser — through the persister's wrapped submit when it is bound, so
 *     the challenge page still gets the fields it needs — and the shopper
 *     sees exactly what they would have seen without this script.
 *
 * Without JavaScript the listener never binds and the form posts normally.
 */

const FORM = '.brand-footer__form';
const MESSAGE = '.brand-footer__form-message';
const EMAIL = '.brand-footer__email';

/** 32 characters of [0-9a-z], the shape Shopify's own persister generates. */
const formKey = () =>
  Array.from(crypto.getRandomValues(new Uint8Array(32)), (n) => (n % 36).toString(36)).join('');

class BrandFooterSignup extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    if (this.#abort.signal.aborted) this.#abort = new AbortController();
    const form = this.querySelector(FORM);
    if (!(form instanceof HTMLFormElement)) return;
    // Capture phase: at the target, capturing listeners run before the
    // bubbling ones, so this goes ahead of Shopify's persister on the same
    // form whichever of the two was bound first.
    form.addEventListener('submit', this.#onSubmit, { capture: true, signal: this.#abort.signal });
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    const form = /** @type {HTMLFormElement} */ (event.currentTarget);

    // Let the browser handle its own validation UI first; only take over a
    // submit that was actually going to go through.
    if (!form.checkValidity()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.dataset.busy) return;
    this.dataset.busy = 'true';

    try {
      // /contact refuses a post that carries no form_key (400, "invalid
      // parameters"). The persister would have minted one on a native submit;
      // mint the same 32-character key here, in the body only, so the form
      // itself is left exactly as Shopify expects to find it on a fallback.
      const body = new FormData(form);
      if (!body.get('form_key')) body.set('form_key', formKey());

      const response = await fetch(form.action, {
        method: 'POST',
        body,
        headers: { Accept: 'text/html' },
      });
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');

      // Shopify re-renders this section with either form.errors or
      // form.posted_successfully?, so its message element is the answer.
      // Anything else — the challenge page, a refusal — is not ours to show.
      const rendered = parsed.querySelector(MESSAGE);
      if (!rendered) {
        this.#submitNatively(form);
        return;
      }

      // The message is lifted as markup, not text, so the tick the section
      // renders on success comes with it. Same-origin, our own section: safe.
      const role = rendered.getAttribute('role') ?? 'status';
      this.#showMessage(form, rendered.innerHTML.trim(), role);

      if (role !== 'alert') {
        const email = form.querySelector(EMAIL);
        if (email instanceof HTMLInputElement) email.value = '';
      }
    } catch {
      // A network failure is the one case this cannot answer for, so hand the
      // submit back to the browser rather than swallowing it silently.
      this.#submitNatively(form);
    } finally {
      delete this.dataset.busy;
    }
  };

  /**
   * The plain browser submit. When Shopify's persister is bound it has
   * replaced `form.submit` with a wrapper that stashes the fields for the
   * challenge page first, so that is the one to call; otherwise the prototype.
   * @param {HTMLFormElement} form
   */
  #submitNatively(form) {
    const submit = typeof form.submit === 'function' ? form.submit : HTMLFormElement.prototype.submit;
    submit.call(form);
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} text - The message markup Shopify rendered.
   * @param {string} role
   */
  #showMessage(form, text, role) {
    let node = form.querySelector(MESSAGE);

    if (!text) {
      node?.remove();
      return;
    }

    if (!node) {
      node = document.createElement('p');
      node.className = MESSAGE.slice(1);
      form.append(node);
    }

    node.setAttribute('role', role);
    node.innerHTML = text;
  }
}

if (!customElements.get('brand-footer-signup')) {
  customElements.define('brand-footer-signup', BrandFooterSignup);
}
